import type { ComponentProps } from '../blocks';
import { CHART_COLORS, CHART_FONT, resolveCssColor, type ChartColors } from './colors';
import { xmlEscape } from './escape';

/** Layout constants for the radial gauge. A square chart area below the title. */
export interface RadialDims {
  /** Overall square viewport width/height of the ring area (excludes the title band). */
  size: number;
  /** Ring center (equal to `size / 2`). */
  cx: number;
  cy: number;
  /** Ring radius (to the stroke centerline). */
  r: number;
  /** Ring/arc stroke width. */
  strokeWidth: number;
}

/**
 * Default dims: a 220×220 ring area. `r` leaves half the stroke inside the box
 * so the rounded caps never clip. The title band (when present) is added on top
 * by the renderer, mirroring the area/split siblings.
 */
export const RADIAL_DIMS: RadialDims = {
  size: 220,
  cx: 110,
  cy: 110,
  r: 92,
  strokeWidth: 18,
};

/** Pure coordinate/path math for a radial gauge — no React, no DOM, no color. */
export interface RadialGeometry {
  /** Filled share in [0, 1] (value / max, clamped). */
  fraction: number;
  /** Foreground arc path, or `null` when full (draw a `<circle>` instead — one `A` can't close 360°). */
  path: string | null;
  /** Whether `fraction` reached a full ring (so the caller draws a circle, not `path`). */
  full: boolean;
  /** Arc start point (12 o'clock, -90°). */
  start: { x: number; y: number };
  /** Arc end point at `fraction`. */
  end: { x: number; y: number };
  cx: number;
  cy: number;
  r: number;
  dims: RadialDims;
}

/** Point on the ring at `deg` degrees, measured clockwise from 12 o'clock. */
function pointAt(cx: number, cy: number, r: number, deg: number): { x: number; y: number } {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

/**
 * Compute the gauge geometry. `fraction = clamp(value / max, 0, 1)`; a
 * non-finite or non-positive `max`, or a non-finite `value`, yields fraction 0
 * (empty track only). The arc starts at 12 o'clock (-90°) and sweeps clockwise
 * by `fraction * 360°`; a full ring returns `path: null` so the caller draws a
 * circle (a single SVG `A` arc cannot express a closed 360° sweep).
 */
export function radialGeometry(value: number, max: number, dims: RadialDims = RADIAL_DIMS): RadialGeometry {
  const { cx, cy, r } = dims;
  const denom = Number.isFinite(max) && max > 0 ? max : NaN;
  const raw = Number.isFinite(value) && Number.isFinite(denom) ? value / denom : 0;
  const fraction = Math.max(0, Math.min(1, raw));
  const start = pointAt(cx, cy, r, -90);
  const endDeg = -90 + fraction * 360;
  const end = pointAt(cx, cy, r, endDeg);
  const full = fraction >= 1;
  // Arc from 12 o'clock sweeping clockwise: sweepFlag=1 (clockwise), and
  // largeArcFlag=1 once past the half circle. A closed 360° ring can't be one
  // arc, so the caller draws a <circle> for `full` instead of using `path`.
  const path =
    fraction <= 0 || full
      ? null
      : `M${start.x.toFixed(2)},${start.y.toFixed(2)} A${r},${r} 0 ${fraction > 0.5 ? 1 : 0} 1 ${end.x.toFixed(2)},${end.y.toFixed(2)}`;
  return { fraction, path, full, start, end, cx, cy, r, dims };
}

/**
 * Standalone, self-contained radial-gauge SVG for rasterization (Slack PNGs).
 * Mirrors the React `Radial` primitive: a muted full-circle track ring, a
 * foreground arc filling clockwise from 12 o'clock, and centered text (the
 * value + unit, with an optional caption beneath). No interactivity/animation.
 */
export function radialSvg(props: ComponentProps['Radial'], colors: ChartColors = CHART_COLORS): string {
  const { title, value, max = 100, unit = '', label } = props;
  const { size, cx, cy, r, strokeWidth } = RADIAL_DIMS;
  const titleH = title ? 34 : 8;
  const totalH = titleH + size;

  const g = radialGeometry(value, max);
  const arcColor = resolveCssColor(props.color, colors);
  const valueText = (Number.isFinite(value) ? value.toLocaleString() : '—') + (unit ? unit : '');

  // Track ring (full circle) + the value arc; a full ring draws as a circle.
  const track = `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${colors.track}" stroke-width="${strokeWidth}"/>`;
  const arc = g.full
    ? `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${arcColor}" stroke-width="${strokeWidth}"/>`
    : g.path
      ? `<path d="${g.path}" fill="none" stroke="${arcColor}" stroke-width="${strokeWidth}" stroke-linecap="round"/>`
      : '';

  const valueEl = `<text x="${cx}" y="${cy}" text-anchor="middle" dominant-baseline="central" font-size="40" font-weight="700" fill="${colors.text}">${xmlEscape(valueText)}</text>`;
  const labelEl = label
    ? `<text x="${cx}" y="${cy + 30}" text-anchor="middle" dominant-baseline="central" font-size="14" fill="${colors.subtext}">${xmlEscape(label)}</text>`
    : '';

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${totalH}" width="${size}" height="${totalH}" font-family='${CHART_FONT}'>` +
    `<rect width="${size}" height="${totalH}" fill="${colors.bg}"/>` +
    (title ? `<text x="0" y="22" font-size="15" font-weight="600" fill="${colors.text}">${xmlEscape(title)}</text>` : '') +
    `<g transform="translate(0 ${titleH})">` +
    track +
    arc +
    valueEl +
    labelEl +
    `</g></svg>`
  );
}
